import { api } from "@/lib/api";
import type {
  IngestionRunsResponse,
  LogsResponse,
  NotifyAdminPayload,
  PipelineAvatarsLcpEnvelope,
  PipelineErrors,
  PipelineLastReceivedResponse,
  PipelineMonitorVerdictResponse,
  PipelineStatus,
  ScraperStatus,
} from "@/lib/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIsTabVisible } from "./use-tab-visible";

export interface ScraperFilters {
  status?: string;
  sort?: string;
  order?: string;
  q?: string;
  // IR-1425 r10: `include=lcp` asks /pipeline/avatars for the lifecycle
  // envelope (see `unwrapPipelineAvatars`). Only the avatars endpoint reads it.
  include?: string;
}

function scraperFilterParams(f: ScraperFilters): string {
  const p = new URLSearchParams();
  if (f.status) p.set("status", f.status);
  if (f.sort) p.set("sort", f.sort);
  if (f.order) p.set("order", f.order);
  if (f.q) p.set("q", f.q);
  if (f.include) p.set("include", f.include);
  const s = p.toString();
  return s ? `?${s}` : "";
}

export function usePipelineStatus() {
  const isVisible = useIsTabVisible();
  return useQuery({
    queryKey: ["pipeline", "status"],
    queryFn: () => api<PipelineStatus>("/api/ops/v1/pipeline/status"),
    refetchInterval: isVisible ? 15_000 : false,
  });
}

export function usePipelineScrapers(filters: ScraperFilters = {}) {
  const isVisible = useIsTabVisible();
  return useQuery({
    queryKey: ["pipeline", "scrapers", filters],
    queryFn: () =>
      api<ScraperStatus[]>(`/api/ops/v1/pipeline/scrapers${scraperFilterParams(filters)}`),
    refetchInterval: isVisible ? 15_000 : false,
  });
}

// IR-1034 A: last + next ingestion-cycle times from the env's Cloud Scheduler job.
export interface IngestionSchedule {
  env: "dev" | "prod";
  schedule: string | null;
  state: string | null;
  last_run_at: string | null;
  next_run_at: string | null;
  degraded: boolean;
}

export function useIngestionSchedule(env: "dev" | "prod") {
  const isVisible = useIsTabVisible();
  return useQuery({
    queryKey: ["pipeline", "ingestion-schedule", env],
    queryFn: () => api<IngestionSchedule>(`/api/ops/v1/pipeline/ingestion-schedule?env=${env}`),
    refetchInterval: isVisible ? 60_000 : false,
  });
}

// IR-1584 (c): the pipeline monitor's own healthy/degraded/critical verdict,
// both environments in one call, each read from its own GCS bucket.
export function useMonitorVerdict() {
  const isVisible = useIsTabVisible();
  return useQuery({
    queryKey: ["pipeline", "monitor-verdict"],
    queryFn: () => api<PipelineMonitorVerdictResponse>("/api/ops/v1/pipeline/monitor-verdict"),
    refetchInterval: isVisible ? 60_000 : false,
  });
}

// IR-1584 (b): last data received, per platform type, per environment.
export function useLastReceived() {
  const isVisible = useIsTabVisible();
  return useQuery({
    queryKey: ["pipeline", "last-received"],
    queryFn: () => api<PipelineLastReceivedResponse>("/api/ops/v1/pipeline/last-received"),
    refetchInterval: isVisible ? 60_000 : false,
  });
}

// IR-1034 B: avatar fleet view (fleet inventory + GCS collection truth + Signal
// heartbeat). Supersedes usePipelineScrapers for the Scrapers panel — surfaces
// not-connected avatars and per-avatar group counts the heartbeat can't.
// IR-1425 r10: with `include: "lcp"` the backend answers with the
// `PipelineAvatarsLcpEnvelope`; otherwise (or on a pre-port backend) the bare
// list. Callers go through `unwrapPipelineAvatars` for either shape.
export function usePipelineAvatars(filters: ScraperFilters = {}) {
  const isVisible = useIsTabVisible();
  return useQuery({
    queryKey: ["pipeline", "avatars", filters],
    queryFn: () =>
      api<ScraperStatus[] | PipelineAvatarsLcpEnvelope>(
        `/api/ops/v1/pipeline/avatars${scraperFilterParams(filters)}`,
      ),
    refetchInterval: isVisible ? 15_000 : false,
  });
}

export function usePipelineErrors() {
  const isVisible = useIsTabVisible();
  return useQuery({
    queryKey: ["pipeline", "errors"],
    queryFn: () => api<PipelineErrors>("/api/ops/v1/pipeline/errors"),
    refetchInterval: isVisible ? 15_000 : false,
  });
}

export function usePipelineErrorDetail(source: string | null) {
  return useQuery({
    queryKey: ["pipeline", "errors", source],
    queryFn: () => api<LogsResponse>(`/api/ops/v1/pipeline/errors/${encodeURIComponent(source!)}`),
    enabled: !!source,
  });
}

export function useNotifyAdmin() {
  return useMutation({
    mutationFn: (data: NotifyAdminPayload) =>
      api("/api/ops/v1/pipeline/notify-admin", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  });
}

export function useTriggerRescrape() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) =>
      api("/api/ops/v1/pipeline/trigger", {
        method: "POST",
        body: JSON.stringify({ group_id: groupId }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pipeline"] }),
  });
}

export function useRetryStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { stage: string; group_id?: string }) =>
      api("/api/ops/v1/pipeline/retry", {
        method: "POST",
        body: JSON.stringify(params),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pipeline"] }),
  });
}

export function useIngestionRuns(env: "dev" | "prod") {
  return useQuery({
    queryKey: ["pipeline", "ingestion-runs", env],
    queryFn: () =>
      api<IngestionRunsResponse>(`/api/ops/v1/pipeline/ingestion-runs?env=${env}&days=7`),
    refetchInterval: 60_000,
  });
}
